import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { X } from "lucide-react";
import { useState } from "react";
import { useAssetsList } from "../../hooks";
import { AssetSearchFilters } from "./assetFilter";

interface AssetSearchFiltersType {
  assetType: string;
  facility: string;
  system: string;
  network: string;
  location: string;
  status: string;
  page: number;
}

const getAssetTypeBadgeConfig = (type: string) => {
  switch (type?.toLowerCase()) {
    case "pipe":
      return {
        variant: "default" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "hydrant":
      return {
        variant: "secondary" as const,
        className: "bg-red-100 text-red-800",
      };
    case "valve":
      return {
        variant: "outline" as const,
        className: "bg-green-100 text-green-800",
      };
    case "meter":
      return {
        variant: "default" as const,
        className: "bg-purple-100 text-purple-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

interface AssetSearchTableProps {
  onAssetSelect?: (asset: any) => void;
  searchLocation?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  showSelection?: boolean;
  linkedAsset: any;
  setLinkedAsset: any;
}

export const AssetSearchTable = ({
  onAssetSelect,
  searchLocation,
  linkedAsset,
  setLinkedAsset,
}: AssetSearchTableProps) => {
  const PAGE_SIZE = 10;
  const remoteUtilityId = getRemoteUtilityId();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<AssetSearchFiltersType>({
    assetType: "all",
    facility: "all",
    system: "all",
    network: "all",
    location: "",
    status: "all",
    page: 1,
  });

  // Format filters for the API call
  const formatFiltersForAPI = () => {
    const apiParams: any = {
      remote_utility_id: remoteUtilityId,
      page: currentPage, // Use currentPage state instead of hardcoded 1
      limit: PAGE_SIZE,
    };

    // Add search term
    if (searchTerm.trim()) {
      apiParams.search = searchTerm;
    }

    // Add filters (only if not 'all' or empty)
    if (filters.assetType && filters.assetType !== "all") {
      apiParams.assetType = filters.assetType;
    }
    if (filters.facility && filters.facility !== "all") {
      apiParams.facility = filters.facility;
    }
    if (filters.system && filters.system !== "all") {
      apiParams.system = filters.system;
    }
    if (filters.network && filters.network !== "all") {
      apiParams.network = filters.network;
    }
    if (filters.location && filters.location.trim()) {
      apiParams.location = filters.location;
    }
    if (filters.status && filters.status !== "all") {
      apiParams.status = filters.status;
    }

    return apiParams;
  };

  // Use the formatted parameters for the API call
  const { data: assetData, isLoading } = useAssetsList(formatFiltersForAPI());

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const handleFiltersChange = (newFilters: AssetSearchFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  const clearFilters = () => {
    setFilters({
      assetType: "all",
      facility: "all",
      system: "all",
      network: "all",
      location: "",
      status: "all",
      page: 1,
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleAssetSelectAction = (row: any) => {
    setLinkedAsset(row);
    if (onAssetSelect) onAssetSelect(row);
  };

  const handleClearSelection = () => {
    setLinkedAsset(null);
    if (onAssetSelect) onAssetSelect(null);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      render: (value: string) => value || "N/A",
    },
    {
      key: "assetTypeDisplay",
      header: "Type",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => getAssetTypeBadgeConfig(value).variant,
        getClassName: (value: string) =>
          getAssetTypeBadgeConfig(value).className,
      },
    },
    {
      key: "facilityName",
      header: "Facility",
      type: "text",
      render: (value: string) => value || "N/A",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "text",
      render: (value: string) => (
        <div
          className={`text-sm font-medium capitalize ${
            value === "In Service"
              ? "text-green-600"
              : value === "Out of Service"
              ? "text-red-600"
              : "text-gray-600"
          }`}
        >
          {value}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      type: "custom",
      render: (_, row) => (
        <button
          onClick={() => handleAssetSelectAction(row)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Select
        </button>
      ),
    },
  ];

  const assets = assetData?.results || [];
  const totalItems = assetData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      {searchLocation?.latitude || searchLocation?.address ? (
        <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
          <strong>Search Location:</strong>{" "}
          {searchLocation?.address ||
            `${searchLocation?.latitude?.toFixed(
              6
            )}, ${searchLocation?.longitude?.toFixed(6)}`}
        </div>
      ) : null}

      {linkedAsset ? (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-md flex justify-between items-center">
          <div className="text-sm">
            <strong>Linked Asset:</strong>{" "}
            {linkedAsset.assetNo || linkedAsset.id} -{" "}
            {linkedAsset.assetName || linkedAsset.name || "N/A"} -{" "}
            {linkedAsset.facilityName || linkedAsset.facility || "N/A"}
          </div>
          <button
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <GenericTable
          data={assets}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search by Asset ID or Name..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          actionsType="buttons"
          bulkActions={[]}
          emptyMessage="No assets found"
          filterPanel={
            <AssetSearchFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
